import { useCallback, useEffect, useRef, useState } from "react";
import { getSpotsInBbox } from "@/lib/api";
import type { Spot } from "@/lib/types";

/**
 * Fetch spots within the map viewport on idle (spec 06 §5).
 *
 * The map calls `setBounds` whenever it becomes idle (moveend/zoomend). The
 * hook debounces the `spots_in_bbox` RPC so rapid pans don't spam the backend,
 * and exposes loading/error state plus the latest spots keyed by id.
 */
export interface BBox {
  minLng: number;
  minLat: number;
  maxLng: number;
  maxLat: number;
}

export interface UseSpots {
  spots: Spot[];
  loading: boolean;
  error: Error | null;
  setBounds: (b: BBox | null) => void;
  refresh: () => void;
}

const DEBOUNCE_MS = 250;

export function useSpots(): UseSpots {
  const [bounds, setBoundsState] = useState<BBox | null>(null);
  const [spots, setSpots] = useState<Spot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const reqId = useRef(0);
  const lastBounds = useRef<BBox | null>(null);

  const fetchSpots = useCallback(async (b: BBox) => {
    const myId = ++reqId.current;
    setLoading(true);
    try {
      const rows = await getSpotsInBbox(b.minLng, b.minLat, b.maxLng, b.maxLat);
      if (myId !== reqId.current) return;
      setSpots(rows);
      setError(null);
    } catch (e) {
      if (myId !== reqId.current) return;
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      if (myId === reqId.current) setLoading(false);
    }
  }, []);

  // Debounce bounds changes (map idle events can fire close together).
  useEffect(() => {
    if (!bounds) return;
    const t = window.setTimeout(() => {
      lastBounds.current = bounds;
      void fetchSpots(bounds);
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [bounds, fetchSpots]);

  const refresh = useCallback(() => {
    if (lastBounds.current) void fetchSpots(lastBounds.current);
  }, [fetchSpots]);

  return {
    spots,
    loading,
    error,
    setBounds: setBoundsState,
    refresh,
  };
}
